import { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as adminService from '@/services/adminService';

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-PT');

const StatCard = ({ label, value, detail, tone = 'gold' }) => {
  const tones = {
    gold: 'border-accent/25 bg-accent/10 text-accent',
    blue: 'border-ocean-light/25 bg-ocean-light/10 text-ocean-light',
    green: 'border-forest-light/25 bg-forest-light/10 text-forest-light',
    red: 'border-energy/25 bg-energy/10 text-energy',
    purple: 'border-gem/25 bg-gem/10 text-gem',
  };

  return (
    <article className={`rounded-lg border p-4 ${tones[tone]}`}>
      <p className="text-xs font-bold uppercase tracking-[0.16em] opacity-70">{label}</p>
      <strong className="mt-2 block font-serif text-3xl font-black text-fg">{formatNumber(value)}</strong>
      <p className="mt-1 text-sm text-fg/45">{detail}</p>
    </article>
  );
};

const ChartCard = ({ title, children }) => (
  <section className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
    <h2 className="font-serif text-xl font-black text-accent">{title}</h2>
    <div className="mt-4 h-72">{children}</div>
  </section>
);

export default function AdminDashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminService.getDashboard()
      .then((res) => {
        setDashboard(res.data.data);
        setError('');
      })
      .catch(() => setError('Não foi possível carregar o dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  const growth = useMemo(() => (dashboard?.growth || []).map((item) => ({
    ...item,
    label: new Date(`${item.date}T00:00:00`).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }),
  })), [dashboard]);

  const topPOIs = useMemo(() => (dashboard?.topPOIs || []).map((poi) => ({
    name: poi.name?.length > 18 ? `${poi.name.slice(0, 18)}...` : poi.name,
    visitas: poi.totalVisits || 0,
    categoria: poi.category,
  })), [dashboard]);

  const fraud = dashboard?.fraud || [];
  const kpis = dashboard?.kpis || {};

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Painel operacional</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Dashboard</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Visão geral do jogo, crescimento, exploração urbana, parceiros e alertas.
          </p>
        </div>
        <div className="rounded-lg border border-accent/20 bg-fg/5 px-4 py-3 text-right">
          <div className="text-xs text-fg/40">Estado</div>
          <div className="font-bold text-forest-light">{loading ? 'A carregar' : 'Atualizado'}</div>
        </div>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-energy/40 bg-energy/10 p-4 text-sm font-bold text-energy">
          {error}
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Jogadores" value={kpis.totalPlayers} detail={`${formatNumber(kpis.activePlayers)} ativos nos últimos 7 dias`} tone="gold" />
        <StatCard label="Novos hoje" value={kpis.newToday} detail="Registos desde o início do dia" tone="green" />
        <StatCard label="Coletas hoje" value={kpis.collectionsToday} detail={`${formatNumber(kpis.totalPOIs)} POIs ativos`} tone="blue" />
        <StatCard label="Fraude" value={kpis.fraudCount} detail="Alertas abertos nos últimos 7 dias" tone={kpis.fraudCount > 0 ? 'red' : 'green'} />
        <StatCard label="Resgates semana" value={kpis.redemptionsWeek} detail={`${formatNumber(kpis.totalRedemptions)} resgates totais`} tone="purple" />
        <StatCard label="Parceiros" value={kpis.totalPartners} detail="Parceiros ativos no ecossistema" tone="gold" />
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <ChartCard title="Crescimento de jogadores">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growth} margin={{ left: -20, right: 10, top: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.75} />
                    <stop offset="95%" stopColor="#D4AF37" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                <YAxis stroke="rgba(255,255,255,0.45)" allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#111827', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 8 }} />
                <Area type="monotone" dataKey="count" name="Novos jogadores" stroke="#D4AF37" strokeWidth={3} fill="url(#growthGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="xl:col-span-2">
          <ChartCard title="Top POIs">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topPOIs} layout="vertical" margin={{ left: 20, right: 10, top: 8, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" horizontal={false} />
                <XAxis type="number" stroke="rgba(255,255,255,0.45)" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.45)" width={105} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#111827', border: '1px solid rgba(58,143,209,0.3)', borderRadius: 8 }} />
                <Bar dataKey="visitas" name="Visitas" fill="#3A8FD1" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif text-xl font-black text-accent">Alertas recentes de fraude</h2>
          <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/55">
            {fraud.length} registo(s)
          </span>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[680px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="py-3">Tipo</th>
                <th className="py-3">Severidade</th>
                <th className="py-3">Estado</th>
                <th className="py-3">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {fraud.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-5 text-fg/45">Nenhum alerta recente.</td>
                </tr>
              )}
              {fraud.slice(0, 8).map((item) => (
                <tr key={item.id}>
                  <td className="py-3 font-bold text-fg/80">{item.type}</td>
                  <td className="py-3">
                    <span className="rounded-full border border-energy/30 bg-energy/10 px-2 py-1 text-xs font-bold text-energy">
                      {item.severity}
                    </span>
                  </td>
                  <td className="py-3 text-fg/55">{item.resolvedAt ? 'Resolvido' : 'Aberto'}</td>
                  <td className="py-3 text-fg/55">{new Date(item.createdAt).toLocaleString('pt-PT')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
